import { useEffect, useImperativeHandle, forwardRef } from "react";
import { useDTMFRealtime } from "@/app/_module/hooks/useDTMFRealtime";
import type { DTMFDigit } from "@/app/_module/services/dtmfService";

interface DTMFPollingWrapperProps {
  callSid: string | null;
  enabled: boolean;
  userId: string | null;
  onDigitsUpdate: (digits: DTMFDigit[]) => void;
  onSpeechProcessingUpdate?: (isProcessing: boolean) => void;
}

// Expose ref methods for clearing digits
export interface DTMFPollingWrapperRef {
  clearDigits: () => void;
}

/**
 * Wrapper component that handles DTMF digit collection for a specific call
 * via Supabase Realtime push (replaced HTTP polling).
 * Uses a dedicated :dtmf channel to avoid collisions with other Realtime subscriptions.
 * Automatically receives customer keypad inputs and updates parent.
 */
export const DTMFPollingWrapper = forwardRef<DTMFPollingWrapperRef, DTMFPollingWrapperProps>(({
  callSid,
  enabled,
  userId,
  onDigitsUpdate,
  onSpeechProcessingUpdate,
}, ref) => {

  const { digits, isSpeechProcessing, clearDigits } = useDTMFRealtime({
    callSid,
    enabled,
    userId,
  });

  // Expose clearDigits to parent via ref
  useImperativeHandle(ref, () => ({
    clearDigits: () => {
      clearDigits();
      onDigitsUpdate([]); // Immediately update parent with empty array
    }
  }), [clearDigits, onDigitsUpdate]);

  // Update parent whenever digits change
  useEffect(() => {
    onDigitsUpdate(digits);
  }, [digits, onDigitsUpdate]);

  // Update parent whenever speech processing state changes
  useEffect(() => {
    if (onSpeechProcessingUpdate) {
      onSpeechProcessingUpdate(isSpeechProcessing);
    }
  }, [isSpeechProcessing, onSpeechProcessingUpdate]);

  // This is a pure logic component, no UI
  return null;
});
